function assertHasField<K extends string>(obj: unknown, key: K): asserts obj is Record<K, unknown> {
  if (typeof obj !== 'object' || obj === null) {
    throw new Error('not an object');
  }
  if (!(key in obj)) {
    throw new Error(`object does not contain ${key}`);
  }
}

function assertHasNumberField<K extends string>(
  obj: unknown,
  key: K
): asserts obj is Record<K, number> {
  assertHasField(obj, key);
  if (typeof obj[key] !== 'number') {
    throw new Error(`${key} is not a number`);
  }
}

function assertHasStringField<K extends string>(
  obj: unknown,
  key: K
): asserts obj is Record<K, string> {
  assertHasField(obj, key);
  if (typeof obj[key] !== 'string') {
    throw new Error(`${key} is not a string`);
  }
}

function assertHasNumberOrNullField<K extends string>(
  obj: unknown,
  key: K
): asserts obj is Record<K, number | null> {
  assertHasField(obj, key);
  if (obj[key] !== null && typeof obj[key] !== 'number') {
    throw new Error(`${key} is not a number or null`);
  }
}

function assertHasStringOrNullField<K extends string>(
  obj: unknown,
  key: K
): asserts obj is Record<K, string | null> {
  assertHasField(obj, key);
  if (obj[key] !== null && typeof obj[key] !== 'string') {
    throw new Error(`${key} is not a string or null`);
  }
}

/* Source of truth is this Protobuf enum:
 * https://github.rbx.com/Roblox/service-contracts/blob/6b9f9e16c26830e67e39a9d493cea016f012d0ec/protos/roblox/useraccess/useraccess/v1/restriction.proto#L16. */
export enum RestrictionSource {
  Invalid = 0,
  Moderation = 1,
  Screentime = 2,
  LocaleUnavailable = 3
}

export type Restriction = {
  source: RestrictionSource;
  moderationStatus: number;
  startTime: Date;
  endTime: Date | null;
  durationSeconds: number | null;
};

function readRestriction(data: unknown): Restriction {
  assertHasNumberField(data, 'source');
  assertHasNumberField(data, 'moderationStatus');
  assertHasStringField(data, 'startTime');
  assertHasStringOrNullField(data, 'endTime');
  assertHasNumberOrNullField(data, 'durationSeconds');

  return {
    ...data,
    startTime: new Date(data.startTime),
    endTime: data.endTime !== null ? new Date(data.endTime) : null,
    durationSeconds: data.durationSeconds
  };
}

export type NotApprovedData = {
  restriction: Restriction | null;
};

export function readNotApprovedData(data: unknown): NotApprovedData {
  assertHasField(data, 'restriction');

  return {
    ...data,
    restriction: data.restriction !== null ? readRestriction(data.restriction) : null
  };
}
